import { usePathname, useSearch as useSearchParams, RouterLink as Link } from "../../router";
import { ChevronLeft, ChevronRight } from "../../icons";
import { useEffect, useMemo, useState, useTransition } from "react";
import {
  DndContext,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { Button } from "../../button";
import { Card, CardContent } from "../../card";
import { Count } from "../../count";
import { cn } from "../../utils";
import type { ScopedTask, ViewScope } from "../../types/projects";
import { SectionHeading } from "../section-heading";
import { SectionView } from "./section-view";
import { TaskCard } from "./task-card";

interface PlannerProps {
  scope: ViewScope;
  monthIso: string;
  scheduled: ScopedTask[];
  unscheduled: ScopedTask[];
  /** Shell-supplied server action for committing due-date moves. */
  onSetDueDate: (
    taskScope: { projectSlug: string; sectionSlug: string },
    taskId: number,
    date: string | null,
  ) => Promise<unknown>;
}

type LaneKey = "overdue" | "this_week" | "next_week" | "later" | "no_date";

const LANES: { key: LaneKey; label: string }[] = [
  { key: "overdue", label: "Overdue" },
  { key: "this_week", label: "This Week" },
  { key: "next_week", label: "Next Week" },
  { key: "later", label: "Later" },
  { key: "no_date", label: "No Date" },
];

function pad(n: number): string {
  return n.toString().padStart(2, "0");
}

function isoOf(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function parseMonth(iso: string): { year: number; month: number } {
  const [y, m] = iso.split("-").map((n) => Number(n));
  return { year: y!, month: m! };
}

function monthLabel(year: number, month: number): string {
  return new Date(year, month - 1, 1).toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
  });
}

function shiftMonth(year: number, month: number, delta: number): string {
  const d = new Date(year, month - 1 + delta, 1);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
}

function buildCalendarCells(year: number, month: number): string[] {
  const first = new Date(year, month - 1, 1);
  const startWeekday = first.getDay();
  const daysInMonth = new Date(year, month, 0).getDate();
  const cells: string[] = [];
  for (let i = 0; i < startWeekday; i += 1) {
    const d = new Date(year, month - 1, -startWeekday + i + 1);
    cells.push(isoOf(d));
  }
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push(`${year}-${pad(month)}-${pad(day)}`);
  }
  while (cells.length % 7 !== 0) {
    const last = cells[cells.length - 1]!;
    const [y, m, d] = last.split("-").map(Number);
    const next = new Date(y!, m! - 1, d! + 1);
    cells.push(isoOf(next));
  }
  return cells;
}

function startOfWeek(d: Date): Date {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  out.setDate(out.getDate() - out.getDay());
  return out;
}

function addDays(d: Date, n: number): Date {
  const out = new Date(d);
  out.setDate(out.getDate() + n);
  return out;
}

function laneFor(
  due: string | null,
  status: string,
  todayIso: string,
  nextWeekStart: string,
  laterStart: string,
): LaneKey {
  if (!due) return "no_date";
  const d = due.slice(0, 10);
  if (d < todayIso && status !== "done" && status !== "cancelled")
    return "overdue";
  if (d >= laterStart) return "later";
  if (d >= nextWeekStart) return "next_week";
  return "this_week";
}

function defaultDateForLane(
  lane: LaneKey,
  today: Date,
  nextWeekStart: Date,
): string | null {
  if (lane === "no_date") return null;
  if (lane === "this_week") return isoOf(today);
  if (lane === "next_week") return isoOf(nextWeekStart);
  if (lane === "later") return isoOf(addDays(nextWeekStart, 7));
  return null;
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export function Planner({
  scope,
  monthIso,
  scheduled,
  unscheduled,
  onSetDueDate,
}: PlannerProps) {
  const { year, month } = parseMonth(monthIso);
  const cells = useMemo(() => buildCalendarCells(year, month), [year, month]);
  // Month-nav hrefs MERGE `?month=` into the live query rather than replacing it
  // with a bare `?month=…` — otherwise, mounted as the Issues ▸ Planner `?sub` view
  // (`/work?area=queue&sub=planner`), prev/next/Today would drop `area`+`sub` and
  // land on the Work dashboard (Work-mode consolidation, audit §3). At the standalone
  // /planner tier the query is empty, so the result is identical to before.
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const monthHref = (nextMonth: string | null): string => {
    const params = new URLSearchParams(searchParams?.toString() ?? "");
    if (nextMonth) params.set("month", nextMonth);
    else params.delete("month"); // Today → current month (the page default)
    const qs = params.toString();
    return qs ? `${pathname}?${qs}` : pathname;
  };

  const todayDate = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const todayIso = useMemo(() => isoOf(todayDate), [todayDate]);
  const nextWeekStart = useMemo(
    () => addDays(startOfWeek(todayDate), 7),
    [todayDate],
  );
  const nextWeekStartIso = useMemo(() => isoOf(nextWeekStart), [nextWeekStart]);
  const laterStartIso = useMemo(
    () => isoOf(addDays(nextWeekStart, 7)),
    [nextWeekStart],
  );

  const allInitial = useMemo(
    () => [...scheduled, ...unscheduled],
    [scheduled, unscheduled],
  );

  const [tasks, setTasks] = useState<ScopedTask[]>(allInitial);
  const fingerprint = useMemo(
    () => allInitial.map((t) => `${t.id}:${t.due_date ?? "_"}`).join("|"),
    [allInitial],
  );
  useEffect(() => {
    setTasks(allInitial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fingerprint]);

  const [, startTransition] = useTransition();
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const byDay = useMemo(() => {
    const out: Record<string, ScopedTask[]> = {};
    for (const t of tasks) {
      if (!t.due_date) continue;
      const day = t.due_date.slice(0, 10);
      (out[day] ??= []).push(t);
    }
    return out;
  }, [tasks]);

  const byLane = useMemo(() => {
    const out: Record<LaneKey, ScopedTask[]> = {
      overdue: [],
      this_week: [],
      next_week: [],
      later: [],
      no_date: [],
    };
    for (const t of tasks) {
      const lane = laneFor(
        t.due_date,
        t.status,
        todayIso,
        nextWeekStartIso,
        laterStartIso,
      );
      out[lane].push(t);
    }
    return out;
  }, [tasks, todayIso, nextWeekStartIso, laterStartIso]);

  function applyMove(taskId: number, newDue: string | null) {
    const moving = tasks.find((t) => t.id === taskId);
    if (!moving) return;
    setTasks((prev) =>
      prev.map((t) => (t.id === taskId ? { ...t, due_date: newDue } : t)),
    );
    const taskScope = {
      projectSlug: moving.project_slug,
      sectionSlug: moving.section_slug,
    };
    startTransition(async () => {
      await onSetDueDate(taskScope, taskId, newDue);
    });
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over) return;
    const activeId = String(active.id);
    if (!activeId.startsWith("task:")) return;
    const taskId = Number(activeId.slice("task:".length));
    if (!Number.isFinite(taskId)) return;

    const overId = String(over.id);
    if (overId.startsWith("day:")) {
      applyMove(taskId, overId.slice("day:".length));
      return;
    }
    if (overId.startsWith("lane:")) {
      const lane = overId.slice("lane:".length) as LaneKey;
      if (lane === "overdue") return;
      const next = defaultDateForLane(lane, todayDate, nextWeekStart);
      applyMove(taskId, next);
      return;
    }
  }

  const monthNav = (
    <>
      <Button asChild variant="outline" size="icon-sm">
        <Link
          href={monthHref(shiftMonth(year, month, -1))}
          aria-label="Previous month"
        >
          <ChevronLeft />
        </Link>
      </Button>
      <Button asChild variant="outline" size="sm">
        <Link href={monthHref(null)}>Today</Link>
      </Button>
      <Button asChild variant="outline" size="icon-sm">
        <Link
          href={monthHref(shiftMonth(year, month, 1))}
          aria-label="Next month"
        >
          <ChevronRight />
        </Link>
      </Button>
    </>
  );

  return (
    <Card>
      <CardContent>
        <DndContext id="excellent-planner" sensors={sensors} onDragEnd={handleDragEnd}>
          <SectionView
            title={
              <span className="type-body font-medium text-foreground">
                {monthLabel(year, month)}
              </span>
            }
            actions={monthNav}
          >
            {/* Recessed onto the page background (surface-1) so the calendar
                reads as a distinct panel against the card (surface-2) it sits
                in; full-strength gridlines keep the grid legible. */}
            <div className="grid grid-cols-7 overflow-hidden rounded-lg border border-border bg-background">
              {WEEKDAYS.map((wd) => (
                <div
                  key={wd}
                  className="border-b border-r border-border bg-card px-2 py-1.5 type-body text-muted-foreground last:border-r-0"
                >
                  {wd}
                </div>
              ))}
              {cells.map((iso) => {
                const [, m] = iso.split("-").map(Number);
                const inMonth = m === month;
                const tasksOnDay = byDay[iso] ?? [];
                return (
                  <DayCell
                    key={iso}
                    iso={iso}
                    inMonth={inMonth}
                    isToday={iso === todayIso}
                    tasks={tasksOnDay}
                    scope={scope}
                  />
                );
              })}
            </div>

            <section className="flex flex-col gap-2">
              <SectionHeading
                as="h3"
                title="Schedule"
                actions={
                  <span className="type-body text-muted-foreground">
                    Drag tasks between lanes to commit due dates
                  </span>
                }
              />
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-5">
                {LANES.map((lane) => (
                  <Lane
                    key={lane.key}
                    laneKey={lane.key}
                    label={lane.label}
                    tasks={byLane[lane.key]}
                    scope={scope}
                    disabled={lane.key === "overdue"}
                  />
                ))}
              </div>
            </section>
          </SectionView>
        </DndContext>
      </CardContent>
    </Card>
  );
}

function DayCell({
  iso,
  inMonth,
  isToday,
  tasks,
  scope,
}: {
  iso: string;
  inMonth: boolean;
  isToday: boolean;
  tasks: ScopedTask[];
  scope: ViewScope;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `day:${iso}` });
  return (
    <div
      ref={setNodeRef}
      className={cn(
        "min-h-24 border-b border-r border-border/60 p-1.5 type-body last:border-r-0 transition-colors",
        !inMonth && "bg-muted/50 text-muted-foreground",
        isToday && "bg-accent/60",
        isOver && "bg-accent",
      )}
    >
      <div
        className={cn(
          "flex items-center justify-between",
          inMonth ? "text-foreground" : "text-muted-foreground",
        )}
      >
        <span>{Number(iso.split("-")[2])}</span>
      </div>
      <ul className="mt-1 flex flex-col gap-1">
        {tasks.map((t) => (
          <DraggableChip key={t.id} task={t} scope={scope} compact />
        ))}
      </ul>
    </div>
  );
}

function Lane({
  laneKey,
  label,
  tasks,
  scope,
  disabled,
}: {
  laneKey: LaneKey;
  label: string;
  tasks: ScopedTask[];
  scope: ViewScope;
  disabled?: boolean;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: `lane:${laneKey}`,
    disabled,
  });
  return (
    <section
      ref={setNodeRef}
      className={cn(
        "flex min-h-32 flex-col gap-2 rounded-lg border border-border bg-card p-3 transition-colors",
        isOver && !disabled && "bg-accent/40",
      )}
    >
      <header className="flex items-center justify-between type-body text-foreground">
        <span>{label}</span>
        <Count value={tasks.length} />
      </header>
      <div className="flex flex-col gap-1.5">
        {tasks.length === 0 ? (
          <p className="rounded-md border border-dashed border-border p-2 type-body text-muted-foreground">
            {disabled ? "Nothing overdue" : "Drop here"}
          </p>
        ) : (
          tasks.map((t) => <DraggableChip key={t.id} task={t} scope={scope} />)
        )}
      </div>
    </section>
  );
}

function DraggableChip({
  task,
  scope,
  compact,
}: {
  task: ScopedTask;
  scope: ViewScope;
  compact?: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({ id: `task:${task.id}` });
  return (
    <div
      ref={setNodeRef}
      style={
        transform
          ? {
              transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
              zIndex: 50,
            }
          : undefined
      }
      className={cn(isDragging && "opacity-70")}
      {...listeners}
      {...attributes}
    >
      {compact ? (
        <CompactChip task={task} />
      ) : (
        <TaskCard task={task} scope={scope} draggable />
      )}
    </div>
  );
}

function CompactChip({ task }: { task: ScopedTask }) {
  return (
    <div className="flex items-center gap-1 rounded-sm border border-border bg-background px-1.5 py-0.5 type-body text-foreground cursor-grab">
      <span
        className="size-1.5 shrink-0 rounded-full"
        style={{
          backgroundColor: task.project_color ?? "var(--muted-foreground)",
        }}
      />
      <span className="truncate">{task.title}</span>
    </div>
  );
}
